/**
 * Bell Notification Service
 * Handles all API interactions for bell notifications (unified notification source)
 */

import apiClient from 'core/services/apiClient';

// Types
export interface BellNotification {
  id: string;
  title: string;
  message: string;
  category: 'announcement' | 'order' | 'lead' | 'task' | 'approval' | 'reminder' | 'alert' | 'expense' | 'attendance' | 'target' | 'system';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  isRead: boolean;
  readAt?: string;
  actionUrl?: string;
  referenceType?: string;
  referenceId?: string;
  senderName?: string;
  senderAvatar?: string;
  createdAt: string;
  icon?: string;
  iconColor?: string;
}

export interface VersionInfo {
  updateAvailable: boolean;
  forceUpdate: boolean;
  updateType: 'optional' | 'recommended' | 'force';
  latestVersion: string;
  clientVersion: string;
  storeUrl?: string;
  updateTitle?: string;
  updateMessage?: string;
  releaseNotes?: string;
  releaseDate?: string;
  isMaintenanceMode: boolean;
  maintenanceMessage?: string;
  featureFlags?: Record<string, boolean>;
}

export interface BellNotificationListResponse {
  notifications: BellNotification[];
  unreadCount: number;
  total: number;
  versionInfo?: VersionInfo | null;
}

export interface BellNotificationStats {
  total: number;
  unread: number;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
}

/**
 * API Endpoints Configuration
 */
const ENDPOINTS = {
  notifications: '/bell-notifications',
  webNotifications: '/bell-notifications/web',
  appNotifications: '/bell-notifications/app',
  recentNotifications: '/bell-notifications/recent',
  unreadCount: '/bell-notifications/unread-count',
  stats: '/bell-notifications/stats',
  markAsRead: (id: string) => `/bell-notifications/${id}/read`,
  markAllAsRead: '/bell-notifications/mark-all-read',
  dismiss: (id: string) => `/bell-notifications/${id}/dismiss`,
  dismissAll: '/bell-notifications/dismiss-all',
  bulkMarkRead: '/bell-notifications/mark-read',
  bulkDismiss: '/bell-notifications/dismiss',
} as const;

/**
 * Bell Notification Service Class
 */
// Get current app version from environment
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

class BellNotificationService {
  /**
   * Get recent notifications for dropdown (includes version check)
   */
  async getRecentNotifications(
    limit: number = 10,
    channel: 'web' | 'app' = 'web'
  ): Promise<BellNotificationListResponse> {
    try {
      const response = await apiClient.get(ENDPOINTS.recentNotifications, {
        params: {
          limit,
          channel,
          platform: 'web',
          clientVersion: APP_VERSION,
        },
      });

      return {
        notifications: response.data?.notifications || response.notifications || [],
        unreadCount: response.data?.unreadCount || response.unreadCount || 0,
        total: response.data?.total || response.total || 0,
        versionInfo: response.data?.versionInfo || response.versionInfo || null,
      };
    } catch (error) {
      console.error('Error fetching recent notifications:', error);
      throw error;
    }
  }

  /**
   * Get all notifications with pagination
   */
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    category?: string;
    isRead?: boolean;
    channel?: 'web' | 'app';
  }): Promise<{
    data: BellNotification[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      const response = await apiClient.get(ENDPOINTS.notifications, { params });

      return {
        data: response.data?.data || response.data || [],
        total: response.data?.total || response.total || 0,
        page: response.data?.page || response.page || params?.page || 1,
        limit: response.data?.limit || response.limit || params?.limit || 20,
      };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Get web-specific notifications
   */
  async getWebNotifications(limit: number = 20): Promise<BellNotificationListResponse> {
    try {
      const response = await apiClient.get(ENDPOINTS.webNotifications, {
        params: { limit },
      });

      return {
        notifications: response.data?.notifications || response.notifications || [],
        unreadCount: response.data?.unreadCount || response.unreadCount || 0,
        total: response.data?.total || response.total || 0,
      };
    } catch (error) {
      console.error('Error fetching web notifications:', error);
      throw error;
    }
  }

  /**
   * Get unread count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await apiClient.get(ENDPOINTS.unreadCount);
      return response.data?.count || response.count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  /**
   * Get notification statistics
   */
  async getStats(): Promise<BellNotificationStats> {
    try {
      const response = await apiClient.get(ENDPOINTS.stats);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }

  /**
   * Mark single notification as read
   */
  async markAsRead(id: string): Promise<void> {
    try {
      await apiClient.post(ENDPOINTS.markAsRead(id));
    } catch (error) {
      console.error(`Error marking notification ${id} as read:`, error);
      throw error;
    }
  }

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(ids: string[]): Promise<void> {
    try {
      await apiClient.post(ENDPOINTS.bulkMarkRead, { notificationIds: ids });
    } catch (error) {
      console.error('Error marking multiple notifications as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      await apiClient.post(ENDPOINTS.markAllAsRead);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Dismiss single notification
   */
  async dismiss(id: string): Promise<void> {
    try {
      await apiClient.delete(ENDPOINTS.dismiss(id));
    } catch (error) {
      console.error(`Error dismissing notification ${id}:`, error);
      throw error;
    }
  }

  /**
   * Dismiss multiple notifications
   */
  async dismissMultiple(ids: string[]): Promise<void> {
    try {
      await apiClient.post(ENDPOINTS.bulkDismiss, { notificationIds: ids });
    } catch (error) {
      console.error('Error dismissing multiple notifications:', error);
      throw error;
    }
  }

  /**
   * Dismiss all notifications
   */
  async dismissAll(): Promise<void> {
    try {
      await apiClient.post(ENDPOINTS.dismissAll);
    } catch (error) {
      console.error('Error dismissing all notifications:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const bellNotificationService = new BellNotificationService();
export default bellNotificationService;
